import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import StudentKlassLessons from '@/components/klass/student/StudentKlassLessons/StudentKlassLessons';
import {
  LESSON_BLOCKS_LIMIT,
  REQUEST_STALE_TIME_IN_MS as staleTime,
} from '@/constants';
import useAuth from '@/data/hook/useAuth';
import useCourseEditing from '@/data/hook/useCourseEditing';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useLessonProgresses from '@/data/hook/useLessonProgresses';
import useScheduledLessons from '@/data/hook/useScheduledLessons';
import useStudentContext from '@/data/hook/useStudentContext';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import { isStaff } from '@/functions/auth';
import { ScheduledLessonTypeEnum } from '@/models/ScheduledLesson';
import { useLessonsBlockWithLessons } from '@/pages/admin/courses/studio/CourseStudio.hooks';
import LoadingView from '@/pages/courses/LoadingView';
import { useMemo } from 'react';
import LessonBar from '../LessonBar/LessonBar';
import CreateLessonListProps from './CreateLessonListProps';

export default function CreateLessonBlockList({
  slugCourseName,
  klassId,
}: CreateLessonListProps) {
  if (klassId)
    return (
      <ScheduledLessonBlocks
        klassId={klassId}
        slugCourseName={slugCourseName}
      />
    );

  return <LessonBlocks slugCourseName={slugCourseName} />;
}

function ScheduledLessonBlocks({
  klassId,
  slugCourseName,
}: Required<Pick<CreateLessonListProps, 'klassId' | 'slugCourseName'>>) {
  const { user } = useAuth();
  const staffView = isStaff(user?.userType);

  if (staffView) {
    return (
      <AdminScheduledLessonBlocks
        klassId={klassId}
        slugCourseName={slugCourseName}
      />
    );
  }

  return <StudentScheduledLessonBlocks slugCourseName={slugCourseName} />;
}

function StudentScheduledLessonBlocks({
  slugCourseName,
}: Required<Pick<CreateLessonListProps, 'slugCourseName'>>) {
  const { queryFn: lessonBlocksQueryFn, queryKey: lessonBlocksQueryKey } =
    lessonBlocksQueryKeys.list(slugCourseName, {
      pageSize: LESSON_BLOCKS_LIMIT,
    });

  const {
    results: lessonBlocks,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteService({
    queryFn: lessonBlocksQueryFn,
    queryKey: lessonBlocksQueryKey,
    enabled: !!slugCourseName,
    staleTime,
  });

  const { currentProgress } = useStudentContext();
  if (!currentProgress) return null;
  return (
    <InfinityList
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      className="gap-4"
    >
      {lessonBlocks.map(lessonBlock => (
        <div key={lessonBlock.id} className="flex flex-col gap-4">
          <Text text={lessonBlock.name} format="rubik-500" />
          <StudentKlassLessons
            progress={currentProgress}
            lessonBlockId={lessonBlock.id}
          />
        </div>
      ))}
    </InfinityList>
  );
}

function AdminScheduledLessonBlocks({
  klassId,
  slugCourseName,
}: Required<Pick<CreateLessonListProps, 'klassId' | 'slugCourseName'>>) {
  const { reorderScheduledLessons } = useCourseEditing();

  const {
    results: scheduledLessons,
    fetchNextPage,
    isFetchingNextPage,
    refetching,
    loading,
    hasNextPage,
  } = useScheduledLessons({ klassId });

  const reorder = reorderScheduledLessons.length
    ? reorderScheduledLessons
    : scheduledLessons;

  if (loading || refetching) {
    return <LoadingView />;
  }

  if (reorder.length) {
    return (
      <InfinityList
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onReachEnd={fetchNextPage}
        className="gap-4"
      >
        {reorder.map((scheduledLesson, i) => {
          const isExtraLesson =
            scheduledLesson.type !== ScheduledLessonTypeEnum.NORMAL;
          return (
            <LessonBar
              color={isExtraLesson ? 'accent' : 'secondary'}
              key={scheduledLesson.id}
              lesson={scheduledLesson.lesson}
              index={i}
              slugCourseName={slugCourseName}
              scheduledLesson={scheduledLesson}
              klassId={klassId}
              isStaff
            />
          );
        })}
      </InfinityList>
    );
  }

  return null;
}

function LessonBlocks({
  slugCourseName,
}: Pick<CreateLessonListProps, 'slugCourseName'>) {
  const {
    lessonBlocksWithLessons,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLessonsBlockWithLessons(slugCourseName);

  const lessonIds = useMemo(
    () =>
      lessonBlocksWithLessons.flatMap(block =>
        block.lessons.map(lesson => lesson.id),
      ),
    [lessonBlocksWithLessons],
  );

  const { lessonProgresses, studentView } = useLessonProgresses({
    lessonIds,
    orderBy: 'lesson_order',
  });

  if (isLoading) {
    return <LoadingView />;
  }

  if (lessonBlocksWithLessons.length) {
    return (
      <InfinityList
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onReachEnd={fetchNextPage}
        className="gap-4"
      >
        {lessonBlocksWithLessons.map(lessonBlock => (
          <div key={lessonBlock.id} className="flex flex-col gap-4">
            <Text text={lessonBlock.name} format="rubik-500" />
            {lessonBlock.lessons.map((lesson, i) => {
              const lessonProgressData = studentView
                ? lessonProgresses.find(
                    ({ lesson: { id: lessonId } }) => lessonId === lesson.id,
                  )
                : undefined;

              return (
                <LessonBar
                  color="secondary"
                  key={lesson.id}
                  lesson={lesson}
                  lessonProgress={lessonProgressData}
                  index={i}
                  slugCourseName={slugCourseName}
                  isStaff={!studentView}
                />
              );
            })}
          </div>
        ))}
      </InfinityList>
    );
  }

  return null;
}
